import React, { useEffect, useMemo, useState } from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import Calculator from '@components/Utilities/Calculator';
import OrdersService from '@api/Orders/OrderCtrl';
import { Order } from '@api/Orders/Order';
import CashFlowsService from '@api/CashFlows/CashFlowCtrl';
import { CashFlowType, NewCashFlow } from '@api/CashFlows/CashFlow';
import { showAlertMessage } from '@utils/Alerts/Message';
import useOpenModal from '@hooks/useOpenModal';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { ModalCreditCardCalculator } from '@components/Collaborator/Modals/ModalCreditCardCalculator';
import { ModalCashCalculator } from '@components/Collaborator/Modals/ModalCashCalculator';
import CashRegisterService from '@api/CashRegister/CashRegisterCtrl';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { setCashRegister } from '@store/krispisStore';
import { currencyFormatter } from '@utils/Numbers';

const CashFlowCalculator: React.FunctionComponent = () => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const { cashRegister } = useSelector((state: RootReducer) => state.krispis);
    const ordersService = useMemo(() => new OrdersService(), []);
    const cashFlowsService = useMemo(() => new CashFlowsService(), []);
    const cashRegisterService = useMemo(() => new CashRegisterService(), []);
    const [amount, setAmount] = useState<number>(0);
    const [description, setDescription] = useState<string>('');
    // To pay orders
    const [orders, setOrders] = useState<Order[]>([]);
    const [orderIdSelected, setOrderIdSelected] = useState<string>('');
    const [orderTotalSelected, setOrderTotalSelected] = useState<number>(0);
    const [creditCardAmount, setCreditCardAmount] = useState<number>(0);
    const [bankTransferAmount, setBankTransferAmount] = useState<number>(0);
    const modalCreditCardCalculator = useOpenModal();
    const modalBankTransferCalculator = useOpenModal();
    const modalCashCalculator = useOpenModal();
    const dispatch = useDispatch();
    const clean = () => {
        setAmount(0);
        setDescription('');
        setOrderIdSelected('');
        setOrderTotalSelected(0);
        setCreditCardAmount(0);
        setBankTransferAmount(0);
    };

    const onFinishPayment = async () => {
        try {
            if (orderIdSelected !== '') {
                const cashAmount = orderTotalSelected - creditCardAmount - bankTransferAmount;
                await ordersService.payOrder(
                    orderIdSelected,
                    cashAmount,
                    creditCardAmount,
                    bankTransferAmount
                );
                await getData();
                clean();
            }
        } catch (error: any) {
            showAlertError(error);
        }
    };
    const onAddCashFlow = async (e: any) => {
        e.preventDefault();
        showLoader();
        try {
            if (orderIdSelected !== '') {
                // Pay order
                if (amount - creditCardAmount - bankTransferAmount > 0) {
                    modalCashCalculator.toggle();
                } else {
                    await onFinishPayment();
                }
            } else if (amount > 0) {
                // No pay order
                // La fecha la pone `saveCashFlow`: el instante real (ver `stampNewCashFlow`).
                // Los GASTOS ya no nacen aquí: tienen su pantalla con categoría
                // (`ExpenseRegister`, plan de Gastos F2). Esto es solo "otro ingreso".
                const cashFlow: NewCashFlow = {
                    type: CashFlowType.Input,
                    amount,
                    description
                };
                await cashFlowsService.saveCashFlow(cashFlow);
                showAlertMessage('Ingreso registrado correctamente');
                clean();
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };
    const openCash = async () => {
        showLoader();
        try {
            const cashRegisterData = await cashRegisterService.openCashRegister(amount);
            dispatch(setCashRegister(cashRegisterData));
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };
    const getData = async () => {
        showLoader();
        try {
            if (branchOfficeSelected?.id) {
                const ordersData = await ordersService.getPendingPaymentOrders(branchOfficeSelected.id);
                setOrders(ordersData);
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    return (
        // Mismo defecto que tenia el panel del pedido (D2): pedia `height="90%"`
        // teniendo la barra arriba. Ahora el form es la caja flex y el contenido
        // scrollea dentro.
        <form
            id="add-cash-flow-form"
            onSubmit={onAddCashFlow}
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
            <Stack flex={1} minHeight={0} overflow="auto" p={{ xs: 2, md: 3 }} spacing={2}>
                <Stack direction="row" spacing={2}>
                    <Typography variant="h6" fontWeight={600} flex={1} alignSelf="center">
                        Ingresos y cobros
                    </Typography>
                    <Stack
                        flex={1.5}
                        direction="row"
                        justifyContent="space-between"
                        border={1}
                        borderColor="divider"
                        borderRadius={4}
                        alignItems="center"
                        px={2}
                    >
                        <Typography variant="h6">$</Typography>
                        <Typography variant="h6" fontWeight={600}>
                            {amount.toFixed(2)}
                        </Typography>
                    </Stack>
                </Stack>
                <Calculator
                    disabled={orderIdSelected !== ''}
                    disableKeyPress={orderIdSelected !== ''}
                    showAmount={false}
                    onChangeValue={setAmount}
                />
                {cashRegister && (
                    <FormControl fullWidth>
                        <InputLabel id="orders-select-label">Pedido por cobrar</InputLabel>
                        <Select
                            labelId="orders-select-label"
                            id="orders-select"
                            value={orderIdSelected}
                            label="Pedido por cobrar"
                            onChange={async (e) => {
                                if (e.target.value === '') {
                                    setOrderIdSelected('');
                                    setOrderTotalSelected(0);
                                    setAmount(0);
                                } else {
                                    const order = await ordersService.getOrder(e.target.value);
                                    setOrderIdSelected(e.target.value);
                                    setOrderTotalSelected(order.total);
                                    setAmount(order.total);
                                }
                            }}
                        >
                            <MenuItem value="">Seleccione pedido por cobrar</MenuItem>
                            {orders.map((order, index) => (
                                <MenuItem key={index} value={order.id}>
                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                        <Typography>{`${order.customer?.profile?.name} - ${order.shortId}`}</Typography>
                                        <Typography>{currencyFormatter(order.total)}</Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {orderIdSelected === '' && cashRegister && (
                    <TextField
                        required
                        fullWidth
                        multiline
                        rows={4}
                        label="Descripción"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                )}
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={600}>
                        Subtotal
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                        {currencyFormatter(amount)}
                    </Typography>
                </Stack>
                {creditCardAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body1" fontWeight={600}>
                            Pago con tarjeta
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {`- ${currencyFormatter(creditCardAmount)}`}
                        </Typography>
                    </Stack>
                )}
                {bankTransferAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body1" fontWeight={600}>
                            Pago con transferencia
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {`- ${currencyFormatter(bankTransferAmount)}`}
                        </Typography>
                    </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h5" fontWeight={600}>
                        Total
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                        {currencyFormatter((amount - creditCardAmount - bankTransferAmount))}
                    </Typography>
                </Stack>
                {cashRegister && (
                    <Stack spacing={2}>
                        {orderTotalSelected > 0 && (
                            <Button
                                onClick={() => modalCreditCardCalculator.toggle()}
                                fullWidth
                                color="secondary"
                                variant="outlined"
                                startIcon={<CreditCardIcon />}
                            >
                                Tarjeta
                            </Button>
                        )}
                        {orderTotalSelected - creditCardAmount > 0 && (
                            <Button
                                onClick={() => modalBankTransferCalculator.toggle()}
                                fullWidth
                                color="secondary"
                                variant="outlined"
                                startIcon={<CreditCardIcon />}
                            >
                                Transferencia
                            </Button>
                        )}
                        <Button form="add-cash-flow-form" variant="contained" color="success" type="submit">
                            {orderIdSelected === ''
                                ? 'Registrar ingreso'
                                : amount - creditCardAmount - bankTransferAmount > 0
                                ? 'Cobro en efectivo'
                                : 'Continuar'}
                        </Button>
                    </Stack>
                )}
                {!cashRegister && (
                    <Button variant="contained" color="success" onClick={openCash}>
                        Abrir caja
                    </Button>
                )}
            </Stack>
            <ModalCreditCardCalculator
                modalCreditCardCalculator={modalCreditCardCalculator}
                total={amount}
                onAddCreditCardAmount={setCreditCardAmount}
            />
            <ModalCreditCardCalculator
                modalCreditCardCalculator={modalBankTransferCalculator}
                total={amount - creditCardAmount}
                onAddCreditCardAmount={setBankTransferAmount}
            />
            <ModalCashCalculator
                modalCashCalculator={modalCashCalculator}
                total={amount - creditCardAmount - bankTransferAmount}
                onContinue={onFinishPayment}
            />
        </form>
    );
};
export default CashFlowCalculator;
